import { apiClient } from './apiClient';
import { createRetryWrapper } from '../utils/retryUtils';
import { createErrorHandler } from '../utils/errorHandlingUtils';
import { cacheManager, CACHE_KEYS } from '../utils/cacheUtils';
import { globalAbortManager } from '../utils/abortControllerUtils';

// Create retry wrapper and error handler for file system operations
const retryWrapper = createRetryWrapper('FileSystemService');
const handleError = createErrorHandler('FileSystemService');

/**
 * Represents a file in the local file system
 */
export interface LocalFile {
  id: string;
  name: string;
  type: 'file' | 'folder';
  size?: number;
  lastModified: Date;
  fullPath: string;
  relativePath: string;
  extension?: string;
  icon?: string;
}

/**
 * Represents a synced folder (OneDrive, SharePoint, etc.)
 */
export interface LocalFolder {
  id: string;
  name: string;
  fullPath: string;
  type: 'onedrive' | 'sharepoint' | 'custom';
  description?: string;
}

/**
 * Tree node structure for file system visualization
 */
export interface FileSystemNode {
  item: LocalFile;
  children?: FileSystemNode[];
  isExpanded?: boolean;
  isLoading?: boolean;
}

/**
 * Local File System service for synced cloud storage folders (OneDrive, Google Drive, SharePoint, etc.)
 * Provides IDE-style file system functionality for the dashboard
 * Handles file operations, folder synchronization, and file browsing
 */
export class LocalFileSystemService {

  /**
   * Unified helper to get files from either directory handle or file list
   * @private
   * @param dirHandle - Directory handle from File System Access API
   * @param fileList - File list from directory input
   * @param relativePath - Relative path within the directory
   * @returns Array of LocalFile objects
   */
  private static async getFilesFromSource(
    dirHandle?: any,
    fileList?: File[],
    relativePath: string = ''
  ): Promise<LocalFile[]> {
    if (dirHandle) {
      return await this.getFilesFromDirectoryPath(dirHandle, relativePath);
    } else if (fileList) {
      return this.getFilesFromFileList(fileList);
    }
    return [];
  }

  /**
   * Gets available synced folders (cloud storage, team sites, etc.)
   * @param abortSignal - Optional AbortSignal for request cancellation
   * @returns Promise resolving to array of synced folders
   * @throws Error on authentication failure or network issues
   */
  static async getSyncFolders(abortSignal?: AbortSignal): Promise<LocalFolder[]> {
    try {
      const cacheKey = CACHE_KEYS.FILESYSTEM_FOLDERS;

      // Try to get from cache first
      const cached = cacheManager.get<LocalFolder[]>(cacheKey);
      if (cached) {
        return cached;
      }

      const folders = await retryWrapper(
        () => apiClient.get<LocalFolder[]>('/api/filesystem/folders'),
        'Fetch sync folders',
        '/api/filesystem/folders',
        'GET',
        abortSignal
      ) || [];

      // Cache the result for 10 minutes (folders don't change frequently)
      cacheManager.set(cacheKey, folders, 10 * 60 * 1000);

      return folders;
    } catch (error) {
      handleError(error, 'getSyncFolders', '/api/filesystem/folders', 'GET');
      return [];
    }
  }

  /**
   * Gets files and folders from a specific synced folder path
   * @param folderId - Unique identifier of the synced folder
   * @param relativePath - Relative path within the folder (default: root)
   * @param parentFolder - Optional parent folder object with directory handle
   * @param abortSignal - Optional AbortSignal for request cancellation
   * @returns Promise resolving to array of files and folders
   * @throws Error on authentication failure, network issues, or invalid folder ID
   */
  static async getFiles(
    folderId: string,
    relativePath: string = '',
    parentFolder?: any,
    abortSignal?: AbortSignal
  ): Promise<LocalFile[]> {
    try {
      // If this is from a native folder selection, handle with directory API
      if (folderId.startsWith('native-') && parentFolder?.dirHandle) {
        // Create abortable operation for native file access
        const operationId = `native_files_${folderId}_${Date.now()}`;
        const controller = globalAbortManager.create(operationId);

        if (abortSignal) {
          abortSignal.addEventListener('abort', () => {
            globalAbortManager.abort(operationId);
          });
        }

        try {
          const result = await this.getFilesFromDirectoryPath(parentFolder.dirHandle, relativePath);
          return result;
        } finally {
          globalAbortManager.abort(operationId);
        }
      }

      // Legacy API approach for synced folders
      const params = new URLSearchParams();
      if (relativePath) params.append('path', relativePath);

      const url = `/api/filesystem/folders/${encodeURIComponent(folderId)}/files?${params.toString()}`;
      const cacheKey = `folder_files_${folderId}_${relativePath || 'root'}`;

      // Try to get from cache first (cache for 5 minutes for file listings)
      const cached = cacheManager.get<LocalFile[]>(cacheKey);
      if (cached) {
        return cached;
      }

      const response = await retryWrapper(
        () => apiClient.get<any[]>(url),
        'Fetch folder files',
        url,
        'GET',
        abortSignal
      );

      // Transform file system response to our file interface
      const files = (response || []).map(this.transformFileItem);

      // Cache the result
      cacheManager.set(cacheKey, files, 5 * 60 * 1000);

      return files;
    } catch (error) {
      const url = `/api/filesystem/folders/${encodeURIComponent(folderId)}/files`;
      handleError(error, 'getFiles', url, 'GET');
      return [];
    }
  }

  // Get files from a specific path within a directory handle
  private static async getFilesFromDirectoryPath(rootHandle: any, path: string): Promise<LocalFile[]> {
    try {
      let currentHandle = rootHandle;

      // Navigate to the specified path
      if (path && path !== '/') {
        const pathParts = path.split('/').filter(part => part);
        for (const part of pathParts) {
          currentHandle = await currentHandle.getDirectoryHandle(part);
        }
      }

      // Read the contents of the current directory
      return await this.getFilesFromDirectoryHandle(currentHandle);
    } catch (error) {
      console.error('Error navigating directory path:', error);
      return [];
    }
  }

  // Get files from a custom folder (using browser APIs)
  static async getCustomFolderFiles(folderData: any): Promise<LocalFile[]> {
    try {
      // Try native APIs first (directory handle or file list)
      if (folderData.dirHandle || folderData.files) {
        return await this.getFilesFromSource(folderData.dirHandle, folderData.files);
      }

      // Legacy fallback: try the API method
      if (folderData.path || typeof folderData === 'string') {
        const folderPath = typeof folderData === 'string' ? folderData : folderData.path;

        const response = await retryWrapper(
          () => apiClient.post<any[]>('/api/filesystem/custom-folder/files', { folderPath }),
          'Fetch custom folder files',
          '/api/filesystem/custom-folder/files',
          'POST'
        );

        return (response || []).map(this.transformFileItem);
      }

      console.warn('No valid folder data provided');
      return [];
    } catch (error) {
      console.error('Error fetching custom folder files:', error);
      return [];
    }
  }

  // Get files from directory handle (File System Access API)
  private static async getFilesFromDirectoryHandle(dirHandle: any): Promise<LocalFile[]> {
    const files: LocalFile[] = [];
    
    try {
      for await (const [name, handle] of dirHandle.entries()) {
        const isFolder = handle.kind === 'directory';
        let size = 0;
        let lastModified = new Date();
        
        if (!isFolder) {
          try {
            const file = await handle.getFile();
            size = file.size;
            lastModified = new Date(file.lastModified);
          } catch (err) {
            // Skip files that can't be read
          }
        }
        
        files.push({
          id: `${dirHandle.name}/${name}`,
          name: name,
          type: isFolder ? 'folder' : 'file',
          size: size,
          lastModified: lastModified,
          fullPath: `${dirHandle.name}/${name}`,
          relativePath: name,
          extension: isFolder ? undefined : name.split('.').pop()?.toLowerCase(),
          icon: LocalFileSystemService.getFileIcon(name, isFolder)
        });
      }
      
      // Sort: folders first, then by name
      return files.sort((a, b) => {
        if (a.type !== b.type) {
          return a.type === 'folder' ? -1 : 1;
        }
        return a.name.localeCompare(b.name);
      });
    } catch (error) {
      console.error('Error reading directory handle:', error);
      return [];
    }
  }

  // Get files from file list (fallback method)
  private static getFilesFromFileList(files: File[]): LocalFile[] {
    const fileMap = new Map<string, LocalFile>();
    const folderSet = new Set<string>();
    
    // Process all files to build directory structure
    for (const file of files) {
      const pathParts = file.webkitRelativePath.split('/');
      const fileName = pathParts[pathParts.length - 1];
      
      // Add the file itself
      if (pathParts.length > 1) {
        const relativePath = pathParts.slice(1).join('/');
        fileMap.set(relativePath, {
          id: file.webkitRelativePath,
          name: fileName,
          type: 'file',
          size: file.size,
          lastModified: new Date(file.lastModified),
          fullPath: file.webkitRelativePath,
          relativePath: relativePath,
          extension: fileName.split('.').pop()?.toLowerCase(),
          icon: LocalFileSystemService.getFileIcon(fileName, false)
        });
      }
      
      // Add intermediate folders
      for (let i = 1; i < pathParts.length - 1; i++) {
        const folderPath = pathParts.slice(1, i + 1).join('/');
        const folderName = pathParts[i];
        
        if (!folderSet.has(folderPath)) {
          folderSet.add(folderPath);
          fileMap.set(folderPath, {
            id: `folder-${folderPath}`,
            name: folderName,
            type: 'folder',
            size: 0,
            lastModified: new Date(),
            fullPath: folderPath,
            relativePath: folderPath,
            extension: undefined,
            icon: LocalFileSystemService.getFileIcon(folderName, true)
          });
        }
      }
    }
    
    // Get only top-level items
    const topLevelItems = Array.from(fileMap.values()).filter(item => 
      !item.relativePath.includes('/')
    );
    
    // Sort: folders first, then by name
    return topLevelItems.sort((a, b) => {
      if (a.type !== b.type) {
        return a.type === 'folder' ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    });
  }

  /**
   * Opens native folder selection dialog using modern browser APIs
   * Uses File System Access API when available, falls back to directory input
   * @returns Promise resolving to folder selection result
   */
  static async selectFolderWithNativeDialog(): Promise<{success: boolean, folderPath?: string, folderName?: string, cancelled?: boolean, error?: string, dirHandle?: any, files?: File[]}> {
    try {
      // Try modern File System Access API first (Chrome/Edge)
      if ('showDirectoryPicker' in window) {
        try {
          const dirHandle = await (window as any).showDirectoryPicker({
            mode: 'read'
          });

          return {
            success: true,
            folderPath: dirHandle.name, // This is just the name, but we'll store the handle
            folderName: dirHandle.name,
            dirHandle: dirHandle // Store the actual directory handle
          };
        } catch (err: any) {
          if (err.name === 'AbortError') {
            return { cancelled: true, success: false };
          }
          throw err;
        }
      }

      // Fallback: Use directory input for browsers that don't support File System Access API
      return new Promise((resolve) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.webkitdirectory = true;
        input.multiple = true;

        input.onchange = (event: any) => {
          const files = event.target.files;
          if (files && files.length > 0) {
            // Get the common directory path from the first file
            const firstFile = files[0];
            const pathParts = firstFile.webkitRelativePath.split('/');
            const folderName = pathParts[0];

            resolve({
              success: true,
              folderPath: folderName,
              folderName: folderName,
              files: Array.from(files) // Store the file list
            });
          } else {
            resolve({ cancelled: true, success: false });
          }
        };

        input.oncancel = () => {
          resolve({ cancelled: true, success: false });
        };

        // Trigger the folder picker
        input.click();
      });

    } catch (error) {
      console.error('Error opening folder dialog:', error);
      return { success: false, error: `Failed to open folder dialog: ${error}` };
    }
  }

  /**
   * Gets root files for a synced folder
   * @param folderId - Unique identifier of the synced folder
   * @param abortSignal - Optional AbortSignal for request cancellation
   * @returns Promise resolving to array of root files
   */
  static async getRootFiles(folderId: string, abortSignal?: AbortSignal): Promise<LocalFile[]> {
    try {
      const url = `/api/filesystem/folders/${encodeURIComponent(folderId)}/files`;
      const cacheKey = `folder_files_${folderId}_root`;

      // Try to get from cache first
      const cached = cacheManager.get<LocalFile[]>(cacheKey);
      if (cached) {
        return cached;
      }

      const response = await retryWrapper(
        () => apiClient.get<any[]>(url),
        'Fetch root files',
        url,
        'GET',
        abortSignal
      );

      const files = (response || []).map(this.transformFileItem);

      // Cache the result
      cacheManager.set(cacheKey, files, 5 * 60 * 1000);

      return files;
    } catch (error) {
      const url = `/api/filesystem/folders/${encodeURIComponent(folderId)}/files`;
      handleError(error, 'getRootFiles', url, 'GET');
      return [];
    }
  }

  /**
   * Searches files within synced folders using the query string
   * @param query - Search query string
   * @param folderId - Optional folder ID to limit search scope
   * @param abortSignal - Optional AbortSignal for request cancellation
   * @returns Promise resolving to array of matching files
   * @throws Error on authentication failure or network issues
   */
  static async searchFiles(query: string, folderId?: string, abortSignal?: AbortSignal): Promise<LocalFile[]> {
    try {
      const params = new URLSearchParams();
      params.append('query', query);
      if (folderId) params.append('folderId', folderId);

      const url = `/api/filesystem/search?${params.toString()}`;
      const cacheKey = `search_${query}_${folderId || 'all'}`;

      // Try to get from cache first (cache search results for 2 minutes)
      const cached = cacheManager.get<LocalFile[]>(cacheKey);
      if (cached) {
        return cached;
      }

      const response = await retryWrapper(
        () => apiClient.get<any[]>(url),
        'Search files',
        url,
        'GET',
        abortSignal
      );

      const files = (response || []).map(this.transformFileItem);

      // Cache the search result
      cacheManager.set(cacheKey, files, 2 * 60 * 1000);

      return files;
    } catch (error) {
      const url = `/api/filesystem/search?${new URLSearchParams({ query, folderId: folderId || '' }).toString()}`;
      handleError(error, 'searchFiles', url, 'GET');
      return [];
    }
  }

  /**
   * Gets file content for preview (text files, small images, etc.)
   * @param filePath - Path to the file to read
   * @param abortSignal - Optional AbortSignal for request cancellation
   * @returns Promise resolving to file content string or null on failure
   */
  static async getFileContent(filePath: string, abortSignal?: AbortSignal): Promise<string | null> {
    try {
      const url = `/api/filesystem/files/content?path=${encodeURIComponent(filePath)}`;
      const cacheKey = `file_content_${filePath}`;

      // Try to get from cache first (cache file content for 10 minutes)
      const cached = cacheManager.get<string>(cacheKey);
      if (cached) {
        return cached;
      }

      const response = await retryWrapper(
        () => apiClient.get<{content: string}>(url),
        'Fetch file content',
        url,
        'GET',
        abortSignal
      );

      const content = response?.content || null;

      // Cache the file content if it exists
      if (content) {
        cacheManager.set(cacheKey, content, 10 * 60 * 1000);
      }

      return content;
    } catch (error) {
      const url = `/api/filesystem/files/content?path=${encodeURIComponent(filePath)}`;
      handleError(error, 'getFileContent', url, 'GET');
      return null;
    }
  }

  // Transform file system item to our LocalFile interface
  private static transformFileItem(item: any): LocalFile {
    const isFolder = item.isDirectory || item.type === 'folder';
    const lastModified = item.lastModified ? new Date(item.lastModified) : new Date();
    const extension = !isFolder ? item.name.split('.').pop()?.toLowerCase() : undefined;
    
    return {
      id: item.fullPath || item.path || item.name,
      name: item.name,
      type: isFolder ? 'folder' : 'file',
      size: item.size,
      lastModified,
      fullPath: item.fullPath || item.path,
      relativePath: item.relativePath || item.name,
      extension,
      icon: LocalFileSystemService.getFileIcon(item.name, isFolder, extension)
    };
  }

  // Get appropriate icon for file type
  private static getFileIcon(fileName: string, isFolder: boolean, extension?: string): string {
    if (isFolder) return 'folder';
    
    const ext = extension || fileName.split('.').pop()?.toLowerCase();
    
    // Document types
    if (['doc', 'docx'].includes(ext || '')) return 'description';
    if (['xls', 'xlsx'].includes(ext || '')) return 'table_chart';
    if (['ppt', 'pptx'].includes(ext || '')) return 'slideshow';
    if (['pdf'].includes(ext || '')) return 'picture_as_pdf';
    
    // Code files
    if (['js', 'ts', 'jsx', 'tsx', 'html', 'css', 'json', 'xml'].includes(ext || '')) return 'code';
    if (['py', 'cs', 'java', 'cpp', 'c', 'h'].includes(ext || '')) return 'code';
    
    // Images
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(ext || '')) return 'image';
    
    // Text files
    if (['txt', 'md', 'log'].includes(ext || '')) return 'text_snippet';
    
    // Archives
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext || '')) return 'archive';
    
    // Default
    return 'description';
  }

  /**
   * Builds file tree structure for IDE-style display
   * Organizes files into hierarchical tree nodes with folders first
   * @param files - Array of LocalFile objects to organize into tree
   * @returns Array of root FileSystemNode objects
   */
  static buildFileTree(files: LocalFile[]): FileSystemNode[] {
    const nodeMap = new Map<string, FileSystemNode>();
    const rootNodes: FileSystemNode[] = [];
    
    // Sort files: folders first, then by name
    const sortedFiles = [...files].sort((a, b) => {
      if (a.type !== b.type) {
        return a.type === 'folder' ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    });
    
    // Create nodes
    sortedFiles.forEach(file => {
      const node: FileSystemNode = {
        item: file,
        children: file.type === 'folder' ? [] : undefined,
        isExpanded: false,
        isLoading: false
      };
      
      nodeMap.set(file.id, node);
      
      // For now, treat all as root nodes
      // In a more complex implementation, you'd build parent-child relationships
      rootNodes.push(node);
    });
    
    return rootNodes;
  }

  /**
   * Formats file size for display with appropriate units (B, KB, MB, GB, TB)
   * @param bytes - File size in bytes
   * @returns Formatted file size string
   */
  static formatFileSize(bytes?: number): string {
    if (!bytes) return '';
    
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = bytes;
    let unitIndex = 0;
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
  }

  /**
   * Formats date for display with relative time (e.g., "2h ago", "1d ago")
   * @param date - Date object to format
   * @returns Formatted date string with relative time when recent
   */
  static formatDate(date: Date): string {
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 48) return '1d ago';
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
    
    return date.toLocaleDateString();
  }

  // Get common sync folder paths (typical Windows locations for cloud storage)
  /**
   * Opens a file with appropriate viewer/application
   * Uses native APIs when available, falls back to browser download
   * @param file - LocalFile object representing the file to open
   * @param dirHandle - Optional directory handle for native file access
   * @throws Error on file access or opening failures
   */
  static async openFile(file: LocalFile, dirHandle?: any): Promise<void> {
    try {
      // If we have a directory handle (from native API), get the file handle
      if (dirHandle) {
        try {
          const pathParts = file.relativePath.split('/');
          let currentHandle = dirHandle;

          // Navigate to the file's directory
          for (let i = 0; i < pathParts.length - 1; i++) {
            currentHandle = await currentHandle.getDirectoryHandle(pathParts[i]);
          }

          // Get the file handle
          const fileName = pathParts[pathParts.length - 1];
          const fileHandle = await currentHandle.getFileHandle(fileName);
          const actualFile = await fileHandle.getFile();

          await this.openFileFromBlob(actualFile, file);
          return;
        } catch (err) {
          // Fall back to legacy method
        }
      }

      // Legacy fallback for synced folders
      const extension = file.extension?.toLowerCase();

      switch (extension) {
        case 'pdf':
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
        case 'svg':
        case 'webp':
        case 'txt':
        case 'md':
        case 'json':
        case 'xml':
        case 'html':
        case 'css':
        case 'js':
        case 'ts':
          // These can be opened directly in browser
          window.open(file.fullPath, '_blank');
          break;

        default:
          this.downloadAndOpen(file);
      }
    } catch (error) {
      console.error('Error opening file:', error);
    }
  }

  // Open file from blob (native API)
  private static async openFileFromBlob(actualFile: File, fileInfo: LocalFile): Promise<void> {
    const url = URL.createObjectURL(actualFile);
    const extension = fileInfo.extension?.toLowerCase();
    
    switch (extension) {
      case 'pdf':
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'svg':
      case 'webp':
      case 'txt':
      case 'md':
      case 'json':
      case 'xml':
      case 'html':
        // These can be opened directly in browser
        window.open(url, '_blank');
        // Clean up the URL after a delay
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        break;
        
      default:
        // Download the file for system default application
        const link = document.createElement('a');
        link.href = url;
        link.download = actualFile.name;
        link.click();
        URL.revokeObjectURL(url);
    }
  }

  // Download and open with system default application
  private static downloadAndOpen(file: LocalFile): void {
    // Create a temporary download link
    const link = document.createElement('a');
    link.href = file.fullPath;
    link.download = file.name;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Gets default sync folder paths for typical Windows cloud storage locations
   * @returns Array of LocalFolder objects for common sync locations
   */
  static getDefaultSyncPaths(): LocalFolder[] {
    const userProfile = process.env.USERPROFILE || 'C:\\Users\\User';
    
    return [
      {
        id: 'onedrive',
        name: 'OneDrive',
        fullPath: `${userProfile}\\OneDrive`,
        type: 'onedrive',
        description: 'Personal OneDrive files'
      },
      {
        id: 'onedrive-business',
        name: 'OneDrive - Prosper Finance',
        fullPath: `${userProfile}\\OneDrive - Prosper Finance`,
        type: 'onedrive',
        description: 'Business OneDrive files'
      },
      {
        id: 'sharepoint-team',
        name: 'Prosper Finance - Documents',
        fullPath: `${userProfile}\\Prosper Finance\\Prosper Digital - Documents`,
        type: 'sharepoint',
        description: 'Team site document library'
      }
    ];
  }
}

// Maintain backwards compatibility
export const FileSystemService = LocalFileSystemService;
export const SharePointService = LocalFileSystemService; // Legacy alias
export type FileSystemFile = LocalFile;
export type SharePointFile = LocalFile; // Legacy alias
export type FileSystemFolder = LocalFolder;
export type SharePointDrive = LocalFolder; // Legacy alias

export default LocalFileSystemService;
